import {useCallback, useMemo, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {BookOpenText, FileText, Image, Languages, Newspaper, ShieldAlert} from "lucide-react";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {formatNumber} from "../../shared/utils/format";
import {
    archiveNews,
    deleteBanner,
    deleteStory,
    loadArchivedNews,
    loadBanners,
    loadContentOperationsSummary,
    loadNews,
    loadNewsDetail,
    loadSlides,
    loadStories,
    reorderBanners,
    reorderStories,
    saveNews,
    uploadBanner,
    uploadStory,
    uploadStoryItem,
} from "./api";
import {canViewAppTexts} from "./appTextsContracts";
import {canMutateContentScope} from "./contracts";
import {canViewCountryDocuments} from "./countryDocumentsContracts";
import {AppTextsWorkspace} from "./components/AppTextsWorkspace";
import {ConfirmDialog} from "./components/ConfirmDialog";
import {CountryDocumentsWorkspace} from "./components/CountryDocumentsWorkspace";
import {MediaWorkspace} from "./components/MediaWorkspace";
import {NewsWorkspace} from "./components/NewsWorkspace";
import {SlidesWorkspace} from "./components/SlidesWorkspace";
import {useContentCountryScope} from "./useContentCountryScope";
import "./content.css";
import {useTranslation} from "../../shared/i18n";


const initialFilters = {search: "", month: "", year: "", city: ""};
// Tabs that pick their own country (the server answers per country) instead of the header scope.
const OWN_COUNTRY_SECTIONS = new Set(["texts", "documents"]);

export const ContentPage = () => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const capabilities = useCapabilities();
    const [section, setSection] = useState("news");
    const [locale, setLocale] = useState("ru");
    const [filters, setFilters] = useState(initialFilters);
    const [result, setResult] = useState(null);
    const {countryScope, activeCountryScopeRef} = useContentCountryScope({
        role: capabilities.user?.role,
        allowedCountryIds: capabilities.scope?.country_ids || [],
    });
    const hasManageCapability = capabilities.hasAction("content.manage");
    const canManage = hasManageCapability && canMutateContentScope({
        role: capabilities.user?.role,
        countryScope,
        actorCountryId: capabilities.country?.id,
    });
    const showAppTexts = canViewAppTexts({role: capabilities.user?.role, hasAction: capabilities.hasAction});
    const showDocuments = canViewCountryDocuments({role: capabilities.user?.role, hasAction: capabilities.hasAction});
    const [appTextsDirty, setAppTextsDirty] = useState(false);
    const [documentsDirty, setDocumentsDirty] = useState(false);
    const [pendingSection, setPendingSection] = useState(null);
    const activeFilters = Object.values(filters).some(Boolean) ? filters : {};
    const enabled = countryScope !== null;

    const summaryQuery = useQuery({
        queryKey: ["admin-content-summary", countryScope],
        queryFn: () => loadContentOperationsSummary(countryScope),
        enabled,
    });
    const activeNewsQuery = useQuery({
        queryKey: ["admin-content-news", "active", countryScope, activeFilters],
        queryFn: () => loadNews({countryId: countryScope, filters: activeFilters}),
        enabled,
        placeholderData: (previousData) => previousData,
    });
    const archiveNewsQuery = useQuery({
        queryKey: ["admin-content-news", "archive", countryScope],
        queryFn: () => loadArchivedNews(countryScope),
        enabled,
    });
    const bannersQuery = useQuery({
        queryKey: ["admin-content-banners", countryScope, locale],
        queryFn: () => loadBanners(locale, countryScope),
        enabled,
    });
    const storiesQuery = useQuery({
        queryKey: ["admin-content-stories", countryScope, locale],
        queryFn: () => loadStories(locale, countryScope),
        enabled,
    });
    const slidesQuery = useQuery({
        queryKey: ["admin-content-slides", locale],
        queryFn: () => loadSlides(locale),
    });

    const invalidate = (...keys) => keys.forEach((queryKey) => {
        void queryClient.invalidateQueries({queryKey});
    });
    const success = (message, ...keys) => {
        setResult({kind: "success", message});
        invalidate(...keys);
    };

    const saveMutation = useMutation({
        mutationFn: (payload) => saveNews({countryId: countryScope, ...payload}),
        onMutate: () => setResult(null),
        onSuccess: (_, payload) => success(
            payload.article_id ? t("sections.content.p1") : t("sections.content.p2"),
            ["admin-content-news"],
            ["admin-content-summary"],
        ),
    });
    const newsDetailMutation = useMutation({
        mutationFn: ({articleId, requestCountryId}) => loadNewsDetail({
            articleId,
            countryId: requestCountryId,
        }),
    });
    const archiveMutation = useMutation({
        mutationFn: archiveNews,
        onMutate: () => setResult(null),
        onSuccess: () => success(t("sections.content.p3"), ["admin-content-news"], ["admin-content-summary"]),
    });
    const uploadBannerMutation = useMutation({
        mutationFn: (values) => uploadBanner({...values, countryId: countryScope}),
        onMutate: () => setResult(null),
        onSuccess: () => success(t("sections.content.p4"), ["admin-content-banners"], ["admin-content-summary"]),
    });
    const deleteBannerMutation = useMutation({
        mutationFn: (values) => deleteBanner({...values, countryId: countryScope}),
        onMutate: () => setResult(null),
        onSuccess: () => success(t("sections.content.p5"), ["admin-content-banners"], ["admin-content-summary"]),
    });
    const reorderBannerMutation = useMutation({
        mutationFn: (items) => reorderBanners({items, countryId: countryScope}),
        onMutate: () => setResult(null),
        onSuccess: () => success(t("sections.content.p6"), ["admin-content-banners"]),
    });
    const uploadStoryMutation = useMutation({
        mutationFn: (values) => uploadStory({...values, countryId: countryScope}),
        onMutate: () => setResult(null),
        onSuccess: () => success(t("sections.content.p7"), ["admin-content-stories"], ["admin-content-summary"]),
    });
    const deleteStoryMutation = useMutation({
        mutationFn: (values) => deleteStory({...values, countryId: countryScope}),
        onMutate: () => setResult(null),
        onSuccess: () => success(t("sections.content.p8"), ["admin-content-stories"], ["admin-content-summary"]),
    });
    const reorderStoryMutation = useMutation({
        mutationFn: (items) => reorderStories({items, countryId: countryScope}),
        onMutate: () => setResult(null),
        onSuccess: () => success(t("sections.content.p9"), ["admin-content-stories"]),
    });
    const uploadStoryItemMutation = useMutation({
        mutationFn: (values) => uploadStoryItem({...values, countryId: countryScope}),
        onMutate: () => setResult(null),
        onSuccess: () => success(t("sections.content.p10"), ["admin-content-stories"], ["admin-content-summary"]),
    });

    // Leaving the texts or documents tab with an unsaved draft asks first; the draft itself
    // stays in sessionStorage either way.
    const selectSection = (next) => {
        const leavingDirty = (section === "texts" && appTextsDirty) || (section === "documents" && documentsDirty);
        if (next !== section && leavingDirty) {
            setPendingSection(next);
            return;
        }
        setSection(next);
    };
    const cancelSectionSwitch = useCallback(() => setPendingSection(null), []);
    const confirmSectionSwitch = () => {
        setSection(pendingSection);
        setPendingSection(null);
    };

    const summary = summaryQuery.data || {};
    const localeOptions = useMemo(() => {
        const locales = (summary.locales || []).map((item) => item.locale).filter(Boolean);
        return locales.length ? Array.from(new Set(locales)) : ["ru", "kk", "ky"];
    }, [summary.locales]);
    const stats = [
        {label: t("sections.content.p11"), value: summary.active_articles, note: t("sections.content.p12", {v0: formatNumber(summary.visible_articles)})},
        {label: t("sections.content.p13"), value: summary.active_banners, note: t("sections.content.p14", {v0: formatNumber(summary.inactive_banners)})},
        {label: "Stories", value: summary.active_stories, note: t("sections.content.p15", {v0: formatNumber(summary.story_items)})},
        {label: t("sections.content.p16"), value: summary.archived_articles, note: t("sections.content.p17")},
    ];
    const mediaMutations = {
        uploadBanner: uploadBannerMutation,
        deleteBanner: deleteBannerMutation,
        reorderBanner: reorderBannerMutation,
        uploadStory: uploadStoryMutation,
        deleteStory: deleteStoryMutation,
        reorderStory: reorderStoryMutation,
        uploadStoryItem: uploadStoryItemMutation,
    };

    return (
        <div className="admin-page content-page">
            <header className="admin-page-header">
                <div>
                    <p className="admin-eyebrow">{t("sections.content.p18")}</p>
                    <h1 className="admin-page-title">{t("sections.content.p19")}</h1>
                    <p className="admin-page-copy">{t("sections.content.p20")}</p>
                </div>
                <div className="admin-pill"><Newspaper size={14} /> {t("sections.content.p21")}</div>
            </header>

            {!canManage && hasManageCapability && !OWN_COUNTRY_SECTIONS.has(section) ? (
                <div className="content-scope-warning" role="alert"><ShieldAlert size={17} /><div><strong>{t("sections.content.p22")}</strong><span>{t("sections.content.p23")}</span></div></div>
            ) : null}

            <section className="content-kpi-strip" aria-label={t("sections.content.p24")}>
                {stats.map((item) => <article key={item.label}><span>{item.label}</span><strong>{formatNumber(item.value)}</strong><small>{item.note}</small></article>)}
            </section>

            <nav aria-label={t("sections.content.p25")} className="content-tabs">
                <button className={section === "news" ? "is-active" : ""} onClick={() => selectSection("news")} type="button"><Newspaper size={16} /> {t("sections.content.p11")}</button>
                <button className={section === "media" ? "is-active" : ""} onClick={() => selectSection("media")} type="button"><Image size={16} /> {t("sections.content.p26")}</button>
                <button className={section === "slides" ? "is-active" : ""} onClick={() => selectSection("slides")} type="button"><BookOpenText size={16} /> {t("sections.content.p27")}</button>
                {showAppTexts ? (
                    <button className={section === "texts" ? "is-active" : ""} onClick={() => selectSection("texts")} type="button"><Languages size={16} /> {t("sections.content.p28")}</button>
                ) : null}
                {showDocuments ? (
                    <button className={section === "documents" ? "is-active" : ""} onClick={() => selectSection("documents")} type="button"><FileText size={16} /> {t("sections.content.p29")}</button>
                ) : null}
            </nav>

            {section === "news" ? (
                <NewsWorkspace
                    activeQuery={activeNewsQuery}
                    archiveMutation={archiveMutation}
                    archiveQuery={archiveNewsQuery}
                    canManage={canManage}
                    filters={filters}
                    detailMutation={newsDetailMutation}
                    countryId={countryScope}
                    activeCountryScopeRef={activeCountryScopeRef}
                    result={result}
                    saveMutation={saveMutation}
                    setFilters={setFilters}
                />
            ) : null}
            {section === "media" ? (
                <MediaWorkspace
                    bannersQuery={bannersQuery}
                    canManage={canManage}
                    locale={locale}
                    localeOptions={localeOptions}
                    mutations={mediaMutations}
                    result={result}
                    setLocale={setLocale}
                    storiesQuery={storiesQuery}
                />
            ) : null}
            {section === "slides" ? (
                <SlidesWorkspace locale={locale} localeOptions={localeOptions} query={slidesQuery} setLocale={setLocale} />
            ) : null}
            {section === "texts" && showAppTexts ? (
                <AppTextsWorkspace headerCountryScope={countryScope} onDirtyChange={setAppTextsDirty} />
            ) : null}
            {section === "documents" && showDocuments ? (
                <CountryDocumentsWorkspace headerCountryScope={countryScope} onDirtyChange={setDocumentsDirty} />
            ) : null}

            <ConfirmDialog
                cancelLabel={t("sections.content.p30")}
                confirmLabel={t("sections.content.p31")}
                copy={t("sections.content.p32")}
                onCancel={cancelSectionSwitch}
                onConfirm={confirmSectionSwitch}
                open={Boolean(pendingSection)}
                title={t("sections.content.p33")}
            />
        </div>
    );
};
